// Audiences: a filter on tags + "not messaged in the last N days" + the channel (SPEC §6). The answer is a list of
// lead codes, with the people left out counted per reason. It is counted again when a campaign starts.
import { z } from 'zod'
import { Channel, Id, IsoDateTime, LeadCode } from './common'
import { LeadSource, TAG_GROUPS } from './leads'
import { PartnerSlug } from './sources'
import { SuppressionReason } from './suppression'

export const AudienceTags = z.object({
  consent: z.array(z.enum(TAG_GROUPS.consent)).optional(),
  contact: z.array(z.enum(TAG_GROUPS.contact)).optional(),
  place: z.array(z.enum(TAG_GROUPS.place)).optional(),
  product: z.array(z.enum(TAG_GROUPS.product)).optional(),
  source: z.array(z.enum(TAG_GROUPS.source)).optional(),
  partner: z.array(PartnerSlug).optional(), // 2026-10-28: any real partner (GET /partners), not a fixed list
  data: z.array(z.enum(TAG_GROUPS.data)).optional(),
  city: z.array(z.enum(TAG_GROUPS.city)).optional(),
  emp: z.array(z.enum(TAG_GROUPS.emp)).optional(),
  score: z.array(z.enum(TAG_GROUPS.score)).optional(),
})
export type AudienceTags = z.infer<typeof AudienceTags>

/** W6: the last campaign results an audience can pick people by (the person's last contact outcome). */
export const LAST_RESULTS = [
  'no answer',
  'no digit',
  'pressed 1',
  'pressed 9',
  'sent',
  'delivered',
  'read',
  'clicked',
  'failed',
  'replied STOP',
] as const
export const LastResult = z.enum(LAST_RESULTS)
export type LastResult = z.infer<typeof LastResult>

/** A test list holds at most this many people (team mobiles, each an Apply now lead with its FF- code). */
export const MAX_TEST_LEADS = 50

export const AudienceFilter = z.object({
  tags: AudienceTags.describe('an empty or missing group means "any"'),
  not_messaged_days: z.number().int().min(0).describe('marketing messages only'),
  last_result: z
    .object({
      outcomes: z.array(LastResult).min(1, 'Pick at least one result.'),
      days: z.number().int().min(1).max(365).describe('within the last N days'),
    })
    .nullable()
    .optional()
    .describe(
      'W6 (audiences from results): only people whose last campaign contact ended with one of these results within N days; null = any'
    ),
  lead_codes: z
    .array(LeadCode)
    .max(MAX_TEST_LEADS, `At most ${MAX_TEST_LEADS} lead codes`)
    .optional()
    .describe('a test list (owner 2026-11-02): only these people, by their FF- codes; empty or missing = anyone'),
  exclude_any_channel: z.boolean().describe('leave out people who stopped on any channel (default)'),
  channel: Channel,
})
export type AudienceFilter = z.infer<typeof AudienceFilter>

/**
 * Why a person was left out: a suppression reason, a marketing message too recently, or a lender disbursed them in the
 * last 30 days (disbursal block, 2026-10-29).
 */
export const ExclusionReason = z.enum([...SuppressionReason.options, 'recently_messaged', 'disbursed_recent'])
export type ExclusionReason = z.infer<typeof ExclusionReason>

export const AudienceSummary = z.object({
  id: Id,
  name: z.string(),
  filter: AudienceFilter,
  size: z.number().int().min(0),
  excluded_total: z.number().int().min(0),
  made_by_name: z.string(),
  at: IsoDateTime,
})
export type AudienceSummary = z.infer<typeof AudienceSummary>

export const AudienceDetail = AudienceSummary.extend({
  counted_at: IsoDateTime,
  excluded: z.array(z.object({ reason: ExclusionReason, count: z.number().int().min(1) })),
  first: z
    .array(z.object({ lead_code: LeadCode, source: LeadSource, tags: z.array(z.string()) }))
    .describe('the first 30 people in the audience'),
})
export type AudienceDetail = z.infer<typeof AudienceDetail>

export const CreateAudienceInput = z.object({
  name: z.string().trim().min(3, 'Give it a name people understand'),
  filter: AudienceFilter,
})
export type CreateAudienceInput = z.infer<typeof CreateAudienceInput>

/** W8: the live count while an audience is made (the builder page): the filter as it stands. */
export const AudienceLiveCountInput = z.object({ filter: AudienceFilter })
export type AudienceLiveCountInput = z.infer<typeof AudienceLiveCountInput>

export const AudienceLiveCount = z.object({
  size: z.number().int().min(0),
  total: z.number().int().min(0).describe('every lead the portal knows (the registry), before any filter'),
  excluded_total: z.number().int().min(0),
  excluded: z.array(z.object({ reason: ExclusionReason, count: z.number().int().min(1) })),
  first: z.array(z.object({ lead_code: LeadCode, source: LeadSource, tags: z.array(z.string()) })).describe('a sample of 30'),
  counted_at: IsoDateTime,
  facets: z.object({
    tags: z.record(z.string(), z.number().int().min(0)).describe('"group:value" → people of the current selection with that tag'),
    results: z
      .record(z.string(), z.number().int().min(0))
      .describe('last contact result (no answer, pressed 1 …) → people of the current selection, within the results window'),
  }),
})
export type AudienceLiveCount = z.infer<typeof AudienceLiveCount>

export interface AudiencesApi {
  listAudiences(): Promise<AudienceSummary[]>
  /** W8: the live count of a filter being made (the builder page), with every chip's count */
  countAudienceNow(input: AudienceLiveCountInput): Promise<AudienceLiveCount>
  /** counts the audience again, now */
  getAudience(id: string): Promise<AudienceDetail>
  createAudience(input: CreateAudienceInput): Promise<AudienceSummary>
}
